import ConfirmDeleteModal from '@/ui/components/confirm-delete-modal/ConfirmDeleteModal';
import DragHandleWithDropdown from '@/ui/components/dragHandleWithDropdown/DragHandleWithDropdown';
import {
  groupHeadingId,
  groupNameId,
} from '@/ui/pages/proposalBuilderPage/util';
import type { UniqueIdentifier } from '@dnd-kit/core';
import {
  Badge,
  Checkbox,
  ConditionalWrapper,
  ContextMenuRoot,
  Field,
  FloatingTooltip,
  IncrementableNumberInput,
  Radio,
  TooltipCore,
  H4,
  Prose,
  Text,
} from '@payaca/ambrosia';
import CurrencyValue from '@payaca/components/currencyValue/CurrencyValue';
import MarkdownCore from '@payaca/components/markdownLabel/MarkdownCore';
import { DragHandle } from '@payaca/dnd';
import { CurrencyValue as TCurrencyValue } from '@payaca/types/currencyTypes';
import UntitledIcon from '@payaca/untitled-icons';
import { clstx } from '@payaca/utilities/cvaMerge';
import { Nullish } from '@payaca/utilities/types';
import { FC, RefAttributes, useState } from 'react';
import { Link } from 'react-router-dom';

const remarkFirstBlock = () => (tree: { children: unknown[] }) => {
  tree.children = tree.children.slice(0, 1);
};

const rehypeClampFirstBlock =
  () =>
  (tree: {
    children: Array<{
      type: string;
      properties?: { className?: string[] | string };
    }>;
  }) => {
    const first = tree.children.find((c) => c.type === 'element');
    if (!first) return;
    first.properties = first.properties ?? {};
    const existing = first.properties.className;
    let existingArr: string[] = [];
    if (Array.isArray(existing)) {
      existingArr = existing;
    } else if (typeof existing === 'string') {
      existingArr = [existing];
    }
    first.properties.className = [...existingArr, 'line-clamp-1'];
  };

export type LineItemRowProps = {
  className?: string;
  variant?: 'default' | 'compact';
  lineItem: {
    id: string;
    internalReference?: Nullish<string>;
    description?: Nullish<string>;
    quantity: number;
  };
  unitPriceExcTax: TCurrencyValue;
  totalIncTax: TCurrencyValue;
  /** Discount amount applied to the item, as a positive value. Hidden when
   * absent or zero. */
  discountAmount?: TCurrencyValue;
  selectionMode?: 'MULTI_CHOICE' | 'OPTIONAL' | 'REQUIRED';
  parentId?: UniqueIdentifier;
  /** Whether the row's selection control is checked. Only relevant for
   * MULTI_CHOICE / OPTIONAL groups. */
  selected?: boolean;
  onSelectedChange?: (selected: boolean) => void;
  onQuantityChange?: (quantity: number | null) => void;
  /** Deletes the item via the row's context menu. The Delete menu item is only
   * offered when this is provided. */
  onDelete?: () => void;
} & (
  | {
      /** Render the row's interactive area as a React Router link to this path. */
      to: string;
      onClick?: never;
    }
  | {
      /** Render the row's interactive area as a button invoking this handler. */
      onClick: () => void;
      to?: never;
    }
);

const LineItemRow: FC<LineItemRowProps & RefAttributes<HTMLDivElement>> = (
  props
) => {
  const {
    className,
    variant = 'default',
    lineItem,
    to,
    onClick,
    unitPriceExcTax,
    totalIncTax,
    discountAmount,
    selectionMode = 'REQUIRED',
    parentId,
    selected,
    onSelectedChange,
    onQuantityChange,
    onDelete,
    ...rest
  } = props;
  const itemLabel = lineItem.internalReference || 'Untitled item';

  const interactiveClassName =
    'appearance-none bg-transparent text-blue-900 hover:no-underline flex items-center gap-4 text-left stretched-link cursor-pointer flex-1 min-w-0 font-normal';
  const interactiveLabel = `Edit/Delete: ${itemLabel}`;

  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [isDragHandleMenuOpen, setIsDragHandleMenuOpen] = useState(false);

  // Delete is offered both via the row's right-click menu and the drag handle
  // dropdown, so the action lives in one place.
  const menuItems = onDelete
    ? [
        {
          label: 'Remove',
          onClick: () => setIsConfirmingDelete(true),
        },
      ]
    : [];

  return (
    <>
      <ContextMenuRoot menuItems={menuItems}>
        <div
          className={clstx(
            'group flex items-center gap-4 bg-white p-2.5 w-full relative rounded-lg',
            'data-[popup-open]:bg-gray-50 hover:bg-gray-50',
            isDragHandleMenuOpen && 'bg-gray-50',
            className
          )}
          {...rest}
        >
          {menuItems.length > 0 ? (
            <DragHandleWithDropdown
              className="pointer-events-auto z-20 -m-2"
              id={lineItem.id}
              label="item"
              items={menuItems}
              onOpenChange={setIsDragHandleMenuOpen}
            />
          ) : (
            <DragHandle
              className="pointer-events-auto z-20"
              as="button"
              label={itemLabel}
              id={lineItem.id}
            />
          )}

          {selectionMode === 'MULTI_CHOICE' && parentId && (
            <Field.Legacy name={groupNameId(parentId)}>
              <Radio
                aria-label={`Select ${itemLabel}`}
                aria-describedby={groupHeadingId(parentId)}
                className="pointer-events-auto z-20"
                checked={!!selected}
                onChange={(e) => onSelectedChange?.(e.target.checked)}
              />
            </Field.Legacy>
          )}

          {selectionMode === 'OPTIONAL' && parentId && (
            <Field.Legacy name={groupNameId(parentId)}>
              <Checkbox
                aria-label={`Select ${itemLabel}`}
                aria-describedby={groupHeadingId(parentId)}
                className="pointer-events-auto z-20"
                checked={!!selected}
                onChange={(e) => onSelectedChange?.(e.target.checked)}
              />
            </Field.Legacy>
          )}

          <ConditionalWrapper
            condition={to !== undefined}
            Wrapper={Link}
            wrapperProps={{
              to: to ?? '',
              className: interactiveClassName,
              'aria-label': interactiveLabel,
            }}
            FallbackWrapper="button"
            fallbackWrapperProps={{
              type: 'button',
              className: interactiveClassName,
              onClick,
              'aria-label': interactiveLabel,
            }}
          >
            <div className="flex-1 flex items-center gap-2 min-w-0">
              {lineItem.internalReference && (
                <div className="shrink-0">
                  <H4>{lineItem.internalReference}</H4>
                </div>
              )}

              <Prose className="min-w-0">
                <MarkdownCore
                  markdown={lineItem.description ?? ''}
                  remarkPlugins={[remarkFirstBlock]}
                  rehypePlugins={[rehypeClampFirstBlock]}
                />
              </Prose>
            </div>

            <div className="flex items-center gap-2">
              {!!discountAmount && discountAmount.value > 0 && (
                <FloatingTooltip>
                  <Badge
                    className="z-20"
                    size="sm"
                    variant="soft"
                    colour="yellow"
                  >
                    <UntitledIcon name="sale-03.3" className="size-4" />
                    <CurrencyValue
                      value={-discountAmount.value}
                      currency={discountAmount.currency}
                    />
                  </Badge>

                  <TooltipCore>Discount</TooltipCore>
                </FloatingTooltip>
              )}
              <Text>
                <CurrencyValue {...totalIncTax} />
              </Text>
            </div>

            <div
              className={clstx(
                'shrink-0',
                '@screen-sm:group-hover:hidden',
                '@screen-sm:group-data-[popup-open]:hidden'
              )}
            >
              <Text>x {lineItem.quantity}</Text>
            </div>
          </ConditionalWrapper>

          {!!onQuantityChange && (
            <div
              className={clstx(
                // intentionally hidden to small screen, the user must click into the proposal item to change the quantity
                'hidden sr-only',
                '@screen-sm:inline-flex',
                '@screen-sm:focus-within:not-sr-only',
                '@screen-sm:group-hover:not-sr-only',
                '@screen-sm:group-data-[popup-open]:not-sr-only'
              )}
            >
              <IncrementableNumberInput
                aria-label={`Quantity for ${itemLabel}`}
                onChange={onQuantityChange}
                min={1}
                wrapperClassName="inline-flex -my-2.5 w-min pointer-events-auto z-20"
                inputClassName="field-sizing-content"
                // field-sizing sizes the input to its value where it works.
                // Firefox reports support but still renders this input too
                // narrow, so floor the field width on Gecko (and any engine
                // lacking field-sizing) to keep the value readable. The floor
                // sits on the wrapper, not the input, so it survives the
                // wrapper's overflow-hidden clip.
                className="supports-[-moz-appearance:none]:min-w-11 not-supports-[field-sizing:content]:min-w-11"
                sizeVariant="xs"
                value={lineItem.quantity}
              />
            </div>
          )}
        </div>
      </ContextMenuRoot>

      <ConfirmDeleteModal
        isOpen={isConfirmingDelete}
        onClose={() => setIsConfirmingDelete(false)}
        onConfirmDelete={() => {
          onDelete?.();
          setIsConfirmingDelete(false);
        }}
        entityName={{ singular: 'item', plural: 'items' }}
        destructiveVerb="remove"
      />
    </>
  );
};

export default LineItemRow;
